import { useMemo, useState } from "react";
import {
  Plus, Search, ChevronUp, ChevronDown, Eye, Pencil, Trash2, Copy,
  Download, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { GlassCard, Button, Spinner, EmptyState } from "../ui";
import { ConfirmDialog } from "../ui/Modal";
import { useToast } from "../ui/Toast";
import {
  useResourceList, useResourceDelete, useResourceBulk, canWrite,
  type ResourceConfig, type ColumnDef,
} from "../../lib/admin/resources";
import { useAuth } from "../../lib/auth";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rec = Record<string, any>;
const PAGE = 25;

function toCsv(cfg: ResourceConfig, rows: Rec[]): string {
  const keys = cfg.columns.map((c) => c.key);
  const head = cfg.columns.map((c) => c.label).join(",");
  const body = rows.map((r) =>
    keys.map((k) => {
      const v = r[k];
      const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    }).join(",")
  );
  return [head, ...body].join("\n");
}

export default function ResourceTable({
  config, onView, onEdit, onCreate,
}: {
  config: ResourceConfig;
  onView: (r: Rec) => void;
  onEdit: (r: Rec) => void;
  onCreate: () => void;
}) {
  const { capabilities, allTenants, tenants } = useAuth();
  const toast = useToast();
  // Per-tenant config is read-only in the all-tenants view — pick a specific ORG
  // to create/edit. Platform resources (users, access, reference data) stay writable.
  const roMode = allTenants && config.section === "tenant";
  const writable = canWrite(config, capabilities) && !roMode;
  const editable = writable && !config.noEdit;

  // In the all-tenants view, prepend a Tenant column so cross-tenant rows are clear.
  const tenantName = useMemo(() => {
    const m = new Map<number, string>();
    tenants.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [tenants]);
  const columns: ColumnDef[] = roMode
    ? [{ key: "tenant", label: "Tenant", render: (r: Rec) => tenantName.get(r.tenant) ?? "—" }, ...config.columns]
    : config.columns;
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState<Rec | null>(null);
  const [bulkDelete, setBulkDelete] = useState(false);

  const { data, isLoading } = useResourceList(config.endpoint, {
    limit: PAGE, offset, ordering: ordering || undefined,
    search: search || undefined,
  });
  const del = useResourceDelete(config.endpoint);
  const bulk = useResourceBulk(config.endpoint);

  const rows: Rec[] = data?.results ?? [];
  const count = data?.count ?? 0;

  function sortBy(key: string) {
    setOrdering((o) => (o === key ? `-${key}` : o === `-${key}` ? "" : key));
    setOffset(0);
  }
  function toggle(id: number) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }
  function runBulk(action: string) {
    bulk.mutate(
      { action, ids: [...selected] },
      {
        onSuccess: () => {
          toast.success(`${selected.size} ${config.label.toLowerCase()} ${action}d.`);
          setSelected(new Set());
        },
        onError: () => toast.error(`Bulk ${action} failed.`),
      }
    );
  }
  function confirmBulkDelete() {
    bulk.mutate(
      { action: "delete", ids: [...selected] },
      {
        onSuccess: () => {
          toast.success(`${selected.size} ${config.label.toLowerCase()} deleted.`);
          setSelected(new Set());
          setBulkDelete(false);
        },
        onError: () => toast.error("Bulk delete failed."),
      }
    );
  }
  function confirmDelete() {
    if (!confirm) return;
    const name = String(confirm[config.titleField] ?? `#${confirm.id}`);
    del.mutate(confirm.id, {
      onSuccess: () => {
        toast.success(`${config.singular} “${name}” deleted.`);
        setConfirm(null);
      },
      onError: () => toast.error(`Could not delete ${config.singular.toLowerCase()}.`),
    });
  }
  function exportCsv() {
    const csv = toCsv(config, rows);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${config.key}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const selectedArr = useMemo(() => [...selected], [selected]);

  return (
    <div className="space-y-3">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            placeholder={`Search ${config.label.toLowerCase()}…`}
            className="w-full rounded-lg border border-white/10 bg-white/5 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-aurora-cyan/40"
          />
        </div>
        <Button variant="ghost" onClick={exportCsv}><span className="inline-flex items-center gap-1.5"><Download size={14} /> CSV</span></Button>
        {writable && (
          <Button onClick={onCreate}><span className="inline-flex items-center gap-1.5"><Plus size={15} /> New {config.singular}</span></Button>
        )}
      </div>

      {/* bulk action bar */}
      {writable && selectedArr.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-aurora-cyan/30 bg-aurora-cyan/5 px-3 py-2 text-sm">
          <span className="text-aurora-cyan">{selectedArr.length} selected</span>
          {config.supportsBulk !== false && <>
            <Button variant="ghost" onClick={() => runBulk("activate")}>Activate</Button>
            <Button variant="ghost" onClick={() => runBulk("deactivate")}>Deactivate</Button>
            <Button variant="danger" onClick={() => setBulkDelete(true)}>Delete</Button>
          </>}
          <button onClick={() => setSelected(new Set())} className="ml-auto text-slatey hover:text-ink"><X size={16} /></button>
        </div>
      )}

      <GlassCard className="p-0">
        {isLoading ? (
          <div className="p-6"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="p-6"><EmptyState>No {config.label.toLowerCase()} found.</EmptyState></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-left text-slatey">
                  {writable && (
                    <th className="w-8 px-3 py-2">
                      <input type="checkbox" checked={selected.size === rows.length} onChange={toggleAll} />
                    </th>
                  )}
                  {columns.map((c) => (
                    <th key={c.key} className="px-3 py-2 font-medium" style={{ width: c.width }}>
                      <button
                        disabled={!c.sortable}
                        onClick={() => c.sortable && sortBy(c.key)}
                        className={`flex items-center gap-1 uppercase tracking-wider ${c.sortable ? "hover:text-ink" : ""}`}
                      >
                        {c.label}
                        {ordering === c.key && <ChevronUp size={11} />}
                        {ordering === `-${c.key}` && <ChevronDown size={11} />}
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    {writable && (
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key} className="px-3 py-2">
                        {c.render ? c.render(r) : String(r[c.key] ?? "—")}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5 text-slatey">
                        <button onClick={() => onView(r)} title="View" className="hover:text-aurora-cyan"><Eye size={14} /></button>
                        {editable && <button onClick={() => onEdit(r)} title="Edit" className="hover:text-pacific"><Pencil size={14} /></button>}
                        {editable && <button onClick={() => onEdit({ ...r, id: undefined })} title="Duplicate" className="hover:text-ink"><Copy size={14} /></button>}
                        {writable && <button onClick={() => setConfirm(r)} title="Delete" className="hover:text-critical"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* pagination */}
        <div className="flex items-center justify-between border-t border-white/10 px-3 py-2 text-xs text-slatey">
          <span>{count.toLocaleString()} total</span>
          <div className="flex items-center gap-2">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="disabled:opacity-30 hover:text-ink"><ChevronLeft size={16} /></button>
            <span className="mono">{Math.floor(offset / PAGE) + 1} / {Math.max(1, Math.ceil(count / PAGE))}</span>
            <button disabled={offset + PAGE >= count} onClick={() => setOffset(offset + PAGE)} className="disabled:opacity-30 hover:text-ink"><ChevronRight size={16} /></button>
          </div>
        </div>
      </GlassCard>

      {/* delete confirmation */}
      {confirm && (
        <ConfirmDialog
          title="Confirm delete"
          confirmLabel="Delete"
          pending={del.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={confirmDelete}
          message={
            <>
              Delete{" "}
              <span className="font-medium text-ink">
                {String(confirm[config.titleField] ?? `#${confirm.id}`)}
              </span>
              ? Where supported this is a soft-delete (deactivate); it is audit-logged either way.
            </>
          }
        />
      )}

      {/* bulk delete confirmation */}
      {bulkDelete && (
        <ConfirmDialog
          title="Delete selected"
          confirmLabel={`Delete ${selected.size}`}
          pending={bulk.isPending}
          onClose={() => setBulkDelete(false)}
          onConfirm={confirmBulkDelete}
          message={
            <>
              Delete <span className="font-medium text-ink">{selected.size}</span> selected{" "}
              {config.label.toLowerCase()}? Where supported these are soft-deleted; every change is
              audit-logged.
            </>
          }
        />
      )}
    </div>
  );
}
